import { SiteRateSchedule_Update } from '@treadinc/horizon-api-spec';
import { cloneDeep, isEqual, isNil, omitBy } from 'lodash-es';

import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';

import type { SiteRateScheduleDTO, SiteRateScheduleOperations } from './SiteRatesForm';
import { hasRealAddOn } from './vendorRateForDriver';

// Filters out add-on assignments that are unchanged from the
// originally loaded snapshot, so the upstream operations hook only PATCHes
// add-ons the user actually edited. New (`isNew`) and deleted (`isDeleted`)
// assignments are always kept; existing ones are kept only when their `addOn`
// payload differs from the snapshot's counterpart matched by `addOn.id`.
export const filterDirtyAddOns = (
  currentList: AddOnAssignmentSchema[] | null | undefined,
  originalList: AddOnAssignmentSchema[] | null | undefined,
): AddOnAssignmentSchema[] => {
  if (!currentList?.length) return [];

  return currentList.filter((current) => {
    if (current.isNew || current.isDeleted) return true;

    const original = originalList?.find((orig) => orig.addOn?.id === current.addOn?.id);
    return !isEqual(current.addOn, original?.addOn);
  });
};

// Same filter as `filterDirtyAddOns` but also returns the form
// indices the kept items came from. The resilient save hook reports add-on
// errors against the *filtered* index, and the caller uses these positions
// to map back to the rate's full add-on list (where per-row error UI lives).
const filterDirtyAddOnsWithIndices = (
  currentList: AddOnAssignmentSchema[] | null | undefined,
  originalList: AddOnAssignmentSchema[] | null | undefined,
): { items: AddOnAssignmentSchema[]; formIndices: number[] } => {
  if (!currentList?.length) return { items: [], formIndices: [] };

  const items: AddOnAssignmentSchema[] = [];
  const formIndices: number[] = [];

  currentList.forEach((current, formIndex) => {
    const keep =
      current.isNew ||
      current.isDeleted ||
      !isEqual(
        current.addOn,
        originalList?.find((orig) => orig.addOn?.id === current.addOn?.id)?.addOn,
      );
    if (keep) {
      items.push(current);
      formIndices.push(formIndex);
    }
  });

  return { items, formIndices };
};

// Determines whether a Site Rate Schedule is dirty relative to its
// originally loaded snapshot. A `null` `original` means we have no snapshot
// (e.g., a row added with an id at runtime, edge case) — treat as dirty so we
// fall back to the legacy unconditional-PATCH behavior rather than silently
// dropping the save.
export const computeSiteRateDirtyState = (
  current: SiteRateScheduleDTO,
  original: SiteRateScheduleDTO | undefined,
): {
  isDirty: boolean;
  dirtyCustomerAddOns: AddOnAssignmentSchema[];
  dirtyVendorAddOns: AddOnAssignmentSchema[];
  dirtyDriverAddOns: AddOnAssignmentSchema[];
} => {
  const dirtyCustomerAddOns = filterDirtyAddOns(
    current.customerAddOns,
    original?.customerAddOns,
  );
  const dirtyVendorAddOns = filterDirtyAddOns(
    current.vendorAddOns,
    original?.vendorAddOns,
  );
  const dirtyDriverAddOns = filterDirtyAddOns(
    current.driverAddOns,
    original?.driverAddOns,
  );

  if (!original) {
    return {
      isDirty: true,
      dirtyCustomerAddOns,
      dirtyVendorAddOns,
      dirtyDriverAddOns,
    };
  }

  const ratesUnchanged = isEqual(
    SiteRateSchedule.deparseUpdateDTO(current),
    SiteRateSchedule.deparseUpdateDTO(original),
  );

  // The toggle isn't part of `deparseUpdateDTO`, so a flip alone leaves
  // `ratesUnchanged === true`. Treat a real user-driven transition as dirty
  // so submit-time `deriveDriverFromVendor` runs and the driver fields
  // catch up to vendor (or back to the user's saved driver values on
  // toggle-off). On a saved card this only ever fires on an actual user click:
  // `resolveUseVendorRateForDriver` applies the feature-flag default to unsaved
  // cards only, so a loaded card starts from the same value on both sides.
  const useVendorRateForDriverChanged =
    Boolean(current.useVendorRateForDriver) !== Boolean(original.useVendorRateForDriver);

  const isDirty =
    !ratesUnchanged ||
    useVendorRateForDriverChanged ||
    dirtyCustomerAddOns.length > 0 ||
    dirtyVendorAddOns.length > 0 ||
    dirtyDriverAddOns.length > 0;

  return {
    isDirty,
    dirtyCustomerAddOns,
    dirtyVendorAddOns,
    dirtyDriverAddOns,
  };
};

// Builds a sparse PATCH body that includes only the SRS rate
// fields the user actually changed against the originally loaded snapshot.
//
// Per-field detection uses *raw* current vs snapshot — that's what tells us
// whether the user actually touched a given field. Fields the user did not
// touch are omitted entirely. For fields the user did touch, the emitted
// value is taken from the *derived* current so a `useVendorRateForDriver`
// toggle sends the mirrored vendor rate (not the form-state null left
// behind by the toggle handler).
//
// Why this matters for clobber prevention: if `useVendorRateForDriver=true`
// resolves via the FF (loaded SRS had no explicit driver), `derived(current)`
// produces a non-null `driver_rate_value` even when the user never touched
// the driver field. Diffing derived-vs-snapshot would then include that
// value in the body and clobber any concurrent driver-rate edit by another
// user. Diffing raw-vs-snapshot correctly identifies that the user did not
// touch the driver field, so it stays out of the body.
//
// Returns `{}` when no SRS PATCH is needed (no-op or add-on-only change);
// the consumer skips the request.
const DRIVER_RATE_FIELDS = ['driver_rate_value', 'driver_rate_type'] as const;

const RATE_VALUE_FIELDS = [
  'customer_rate_value',
  'vendor_rate_value',
  'driver_rate_value',
  'pw_on_site_rate',
  'total_fringe_rate',
] as const;

const omitRedactedRateValues = (body: SiteRateSchedule_Update) =>
  omitBy(body, (value, key) => {
    const isRateValueField = (RATE_VALUE_FIELDS as readonly string[]).includes(key);
    return value === null && isRateValueField;
  }) as Partial<SiteRateSchedule_Update>;

const computeSparseUpdateBody = (
  rawCurrent: SiteRateScheduleDTO,
  derivedCurrent: SiteRateScheduleDTO,
  original: SiteRateScheduleDTO | undefined,
  canViewRate: boolean,
): Partial<SiteRateSchedule_Update> => {
  if (!original) {
    // No snapshot — fall back to the legacy full body so we don't silently
    // drop the save for an item the snapshot somehow missed.
    const fullBody = SiteRateSchedule.deparseUpdateDTO(derivedCurrent);

    // Without `view_rate` a blank rate value is ambiguous: the server omits
    // the values this user may not see, so we cannot tell "redacted" from
    // "the user cleared it". With no snapshot to diff against, omit only the
    // redacted rate fields — an unrelated field the user genuinely cleared
    // still reaches the server as an explicit null.
    return canViewRate ? fullBody : omitRedactedRateValues(fullBody);
  }

  const rawCurrentBody = SiteRateSchedule.deparseUpdateDTO(rawCurrent) as Record<
    string,
    unknown
  >;
  const originalBody = SiteRateSchedule.deparseUpdateDTO(original) as Record<
    string,
    unknown
  >;
  const derivedCurrentBody = SiteRateSchedule.deparseUpdateDTO(derivedCurrent) as Record<
    string,
    unknown
  >;

  // A bare user-driven toggle change (no field edits) leaves raw == original
  // but flips the derived driver fields. Force the driver columns into the
  // sparse body in that case so the server's driver values catch up to
  // vendor on toggle-on (or back to the user's saved driver values on
  // toggle-off). Only fires on a real transition: a loaded card never starts
  // out checked, because the feature-flag default is restricted to unsaved
  // cards in `resolveUseVendorRateForDriver`.
  const useVendorRateForDriverChanged =
    Boolean(rawCurrent.useVendorRateForDriver) !==
    Boolean(original.useVendorRateForDriver);

  if (isEqual(rawCurrentBody, originalBody) && !useVendorRateForDriverChanged) {
    return {};
  }

  const sparse: Record<string, unknown> = {};
  for (const key of Object.keys(rawCurrentBody)) {
    const userTouched = !isEqual(rawCurrentBody[key], originalBody[key]);
    // Derivation only runs on a toggle flip or a vendor-side edit, so a
    // derived driver field that differs from the snapshot is always an
    // intended re-mirror rather than stale form state.
    const includeFromDerivation =
      (DRIVER_RATE_FIELDS as readonly string[]).includes(key) &&
      !isEqual(derivedCurrentBody[key], rawCurrentBody[key]) &&
      !isEqual(derivedCurrentBody[key], originalBody[key]);
    // A blank rate value from a user who cannot see rates is a redaction, not
    // an edit — never write it back.
    const clearsRedactedRateValue =
      !canViewRate &&
      (RATE_VALUE_FIELDS as readonly string[]).includes(key) &&
      derivedCurrentBody[key] === null;
    if ((userTouched || includeFromDerivation) && !clearsRedactedRateValue) {
      sparse[key] = derivedCurrentBody[key];
    }
  }
  return sparse as Partial<SiteRateSchedule_Update>;
};

// Snapshot clone for the dirty check. MUST preserve prototypes — `structuredClone`
// drops them, so a nested model instance (a fuel-surcharge schedule on an add-on)
// became a plain object and `isEqual`, which compares constructors, never matched.
// The card then read as dirty with no user edit and opened the rate-change drawer.
export const cloneSiteRateSnapshot = (rate: SiteRateScheduleDTO) => cloneDeep(rate);

// Builds the `Map<id, snapshot>` used by the dirty check. Excludes
// rows without an id (those always go to `toCreate`).
export const buildSiteRateSnapshotMap = (
  rates: SiteRateScheduleDTO[],
): Map<string, SiteRateScheduleDTO> => {
  const map = new Map<string, SiteRateScheduleDTO>();
  for (const rate of rates) {
    if (rate.id) {
      map.set(rate.id, cloneSiteRateSnapshot(rate));
    }
  }
  return map;
};

// Treat raw "" the same as null/undefined: `form.watch()` returns raw field
// values without the yup transform, so cleared numeric inputs surface as empty
// strings. Without this check, a duplicated card with every rate cleared
// would route to `toCreate` and produce an empty SRS server-side.
const hasNumericValue = (value: number | string | null | undefined) =>
  value !== undefined && value !== null && value !== '';

const isValidRate = (rate: SiteRateScheduleDTO['customerRate']) =>
  hasNumericValue(rate?.rate as number | string | null | undefined);

// The backend accepts a Site Rate Schedule whose rate fields are all null, so a
// row carrying only add-ons is a real record rather than an empty one.
const hasAnyRealAddOn = (item: SiteRateScheduleDTO) =>
  hasRealAddOn(item.customerAddOns) ||
  hasRealAddOn(item.vendorAddOns) ||
  hasRealAddOn(item.driverAddOns);

const hasAnyRateData = (item: SiteRateScheduleDTO) =>
  Boolean(
    item.name ||
    item.pickupSite?.value ||
    item.dropoffSite?.value ||
    item.material?.value ||
    item.equipmentType?.value ||
    isValidRate(item.customerRate) ||
    isValidRate(item.vendorRate) ||
    isValidRate(item.driverRate) ||
    !isNil(item.vendorPayCommissionPercentage) ||
    !isNil(item.driverPayCommissionPercentage) ||
    item.jurisdiction ||
    hasNumericValue(item.quantity as number | string | null | undefined) ||
    hasNumericValue(item.pwOnSiteRate as number | string | null | undefined) ||
    hasNumericValue(item.totalFringeRate as number | string | null | undefined) ||
    hasAnyRealAddOn(item),
  );

// Mirror the order form's submit-time behavior: when the user has checked
// "Use vendor rate and add-ons for driver rate" on a rate card, persist the
// driver rate / add-ons as copies of the vendor values rather than the form's
// stale driver fields. This keeps the saved record internally consistent
// (driver === vendor) and matches `deriveDriverRateFromVendorRate` in the
// order flow.
const deriveDriverFromVendor = (item: SiteRateScheduleDTO): SiteRateScheduleDTO => {
  if (item.useVendorRateForDriver !== true) return item;

  const derivedDriverRate: SiteRateScheduleDTO['driverRate'] = item.vendorRate
    ? {
        rate: item.vendorRate.rate,
        type: item.vendorRate.type ?? null,
      }
    : { rate: undefined, type: null };

  // Mark previously-saved driver add-on assignments for deletion, drop
  // new-but-unsaved ones, and create fresh driver-side assignments
  // referencing the same add-on types as vendor's.
  const existingDriverAddOns = item.driverAddOns ?? [];
  const driverAddOnDeletions = existingDriverAddOns
    .filter((addOn) => Boolean(addOn.addOn?.id) && !addOn.isNew)
    .map((addOn) => ({ ...addOn, isDeleted: true }));

  const newDriverAddOns = (item.vendorAddOns ?? [])
    .filter((addOn) => !addOn.isDeleted)
    .map((addOn) => ({
      ...addOn,
      isNew: true,
      isDeleted: false,
      addOn: addOn.addOn ? { ...addOn.addOn, id: '' } : addOn.addOn,
    }));

  return {
    ...item,
    driverRate: derivedDriverRate,
    driverAddOns: [...driverAddOnDeletions, ...newDriverAddOns],
  };
};

interface BuildSiteRateScheduleOperationsArgs {
  siteRatesList: SiteRateScheduleDTO[];
  originalSiteRates: Map<string, SiteRateScheduleDTO>;
  canViewRate?: boolean;
}

// Builds the create/update/delete operations list for the user's
// pending Site Rate Schedule edits. For items already on the server (have an
// `id`), pairs each dirty SRS with a *sparse* PATCH body containing only the
// fields the user actually changed against the snapshot — preventing stale
// form state from clobbering rate fields edited concurrently by another user.
// An empty sparse body means "no SRS PATCH"; the consumer still processes
// any dirty add-on assignments attached to the SRS.
//
// Derivation runs on the raw item (not the filtered one) so the vendor →
// driver mirror sees the full vendor add-on list when
// `useVendorRateForDriver=true`; the dirty-add-on filter then runs **after**
// derivation so the synthesized driver add-on deletes/creates flow through
// to the API.
export const buildSiteRateScheduleOperations = ({
  siteRatesList,
  originalSiteRates,
  canViewRate = true,
}: BuildSiteRateScheduleOperationsArgs): SiteRateScheduleOperations => {
  const pushUpdate = (
    obj: SiteRateScheduleOperations,
    rawItem: SiteRateScheduleDTO,
    original: SiteRateScheduleDTO | undefined,
  ) => {
    // A saved card whose driver rate already equals its vendor rate resolves to
    // matching on open, so mirroring on every dirty save would rewrite the
    // driver add-ons of a card the user only edited elsewhere. Mirror when the
    // toggle changed, the vendor side the driver tracks changed, or the user
    // edited the driver side itself — toggling off, editing the driver, and
    // toggling back on leaves the toggle equal to its snapshot, so without the
    // driver comparison those edits would save under a "matching" UI.
    const toggleChanged =
      Boolean(rawItem.useVendorRateForDriver) !==
      Boolean(original?.useVendorRateForDriver);
    const vendorSideChanged =
      !isEqual(rawItem.vendorRate, original?.vendorRate) ||
      !isEqual(rawItem.vendorAddOns, original?.vendorAddOns);
    const driverSideChanged =
      !isEqual(rawItem.driverRate, original?.driverRate) ||
      !isEqual(rawItem.driverAddOns, original?.driverAddOns);
    const derivedItem =
      toggleChanged || vendorSideChanged || driverSideChanged
        ? deriveDriverFromVendor(rawItem)
        : rawItem;
    const customer = filterDirtyAddOnsWithIndices(
      derivedItem.customerAddOns,
      original?.customerAddOns,
    );
    const vendor = filterDirtyAddOnsWithIndices(
      derivedItem.vendorAddOns,
      original?.vendorAddOns,
    );
    const driver = filterDirtyAddOnsWithIndices(
      derivedItem.driverAddOns,
      original?.driverAddOns,
    );

    const rate = {
      ...derivedItem,
      customerAddOns: customer.items,
      vendorAddOns: vendor.items,
      driverAddOns: driver.items,
    };

    obj.toUpdate.push({
      rate,
      body: computeSparseUpdateBody(rawItem, derivedItem, original, canViewRate),
      dirtyAddOnFormIndices: {
        customer: customer.formIndices,
        vendor: vendor.formIndices,
        driver: driver.formIndices,
      },
    });
  };

  return siteRatesList.reduce(
    (obj, rawItem) => {
      if (rawItem.isDeleted) {
        obj.toDelete.push(rawItem);
      } else if (rawItem.id) {
        // A rate-card rule can exist without a site. An empty row only means
        // "the user emptied it" when the rate values that could have filled it
        // were actually shown to this user — without `view_rate` a blank row
        // still counts as having visible content, so it takes the update path
        // instead, where the sparse body comes out empty (no PATCH) but any
        // add-on edits still reach the server.
        const original = originalSiteRates.get(rawItem.id);
        // Only an edit in this save can empty a saved card. Restoring commission
        // inheritance still patches the card, even if that leaves it empty.
        if (!computeSiteRateDirtyState(rawItem, original).isDirty) return obj;
        const restoresCommissionInheritance =
          (rawItem.vendorPayCommissionPercentage === null &&
            !isNil(original?.vendorPayCommissionPercentage)) ||
          (rawItem.driverPayCommissionPercentage === null &&
            !isNil(original?.driverPayCommissionPercentage));
        const rowMayHaveVisibleContent =
          hasAnyRateData(rawItem) || !canViewRate || restoresCommissionInheritance;

        if (rowMayHaveVisibleContent) {
          pushUpdate(obj, rawItem, original);
        } else {
          obj.toDelete.push(rawItem);
        }
      } else if (hasAnyRateData(rawItem)) {
        obj.toCreate.push(deriveDriverFromVendor(rawItem));
      }

      return obj;
    },
    {
      toCreate: [],
      toUpdate: [],
      toDelete: [],
    } as SiteRateScheduleOperations,
  );
};
